import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import DataWarehousesTable from "./DataWarehousesTable";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

// --- Interface Definitions ---
interface DataWarehouseParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface DataWarehouseApplicationInfo {
    id: string;
    applicationName: string;
}

interface DataWarehouseSpecificDetails {
    projectId?: string;
    datasetId?: string;
    region?: string;
    pricingModel?: string;
}

interface DataWarehouseJob {
    id: string;
    name: string;
    application?: DataWarehouseApplicationInfo;
    cloudProvider?: string;
    solutionCategoryType?: string;
    costLabelPairs?: { cost: string; label: string }[];
    status: 'pending' | 'active' | 'rejected' | string;
    details?: {
        dataWarehouse?: DataWarehouseSpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: DataWarehouseParticipantInfo | null;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof DataWarehouseJob | "solutionOwner.name" | "details.dataWarehouse.region" | "application.applicationName" | string;
    groupBy: string;
}

const DataWarehouses: React.FC = () => {
    const [allDataWarehouses, setAllDataWarehouses] = useState<DataWarehouseJob[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);

    const initialColumnFilters: Record<string, boolean> = {
        name: true,
        application: true,
        cloudProvider: true,
        dataWarehouseType: true,
        region: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetDataWarehouses {
                      getDataWarehouses {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        cloudProvider
                        solutionCategoryType
                        status
                        details {
                          dataWarehouse {
                            region
                          }
                        }
                        createdAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const dataWarehousesData = response.getDataWarehouses;
                if (dataWarehousesData) {
                    const processedWarehouses = dataWarehousesData.map((dw: any): DataWarehouseJob => ({
                        id: dw.id,
                        name: dw.name || "Unnamed Warehouse",
                        application: dw.application ? {
                            id: dw.application.id,
                            applicationName: dw.application.applicationName,
                        } : undefined,
                        cloudProvider: dw.cloudProvider,
                        solutionCategoryType: dw.solutionCategoryType,
                        status: (dw.status || 'pending').toLowerCase(),
                        details: dw.details,
                        createdAt: dw.createdAt,
                        solutionOwner: dw.solutionOwner ? {
                            id: dw.solutionOwner.id,
                            name: dw.solutionOwner.name,
                            googleProfilePicture: dw.solutionOwner.googleProfilePicture,
                            
                        } : null,
                    }));
                    setAllDataWarehouses(processedWarehouses);

                    let dynamicSearchOptions: SearchOptions[] = processedWarehouses
                        .map((warehouse: DataWarehouseJob) => [
                            { category: "name", groupBy: "WAREHOUSE NAME", chipLabel: `Name: ${warehouse.name}`, dropdownLabel: warehouse.name },
                            ...(warehouse.application ? [{ category: "application.applicationName" as any, groupBy: "APPLICATION", chipLabel: `App: ${warehouse.application.applicationName}`, dropdownLabel: warehouse.application.applicationName }] : []),
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${warehouse.status}`, dropdownLabel: warehouse.status.charAt(0).toUpperCase() + warehouse.status.slice(1) },
                            ...(warehouse.cloudProvider ? [{ category: "cloudProvider" as any, groupBy: "CLOUD PROVIDER", chipLabel: `Cloud: ${warehouse.cloudProvider}`, dropdownLabel: warehouse.cloudProvider }] : []),
                            ...(warehouse.solutionCategoryType ? [{ category: "solutionCategoryType" as any, groupBy: "WAREHOUSE TYPE", chipLabel: `Type: ${warehouse.solutionCategoryType}`, dropdownLabel: warehouse.solutionCategoryType }] : []),
                            ...(warehouse.details?.dataWarehouse?.region ? [{ category: "details.dataWarehouse.region" as any, groupBy: "REGION", chipLabel: `Region: ${warehouse.details.dataWarehouse.region}`, dropdownLabel: warehouse.details.dataWarehouse.region }] : []),
                            ...(warehouse.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${warehouse.solutionOwner.name}`, dropdownLabel: warehouse.solutionOwner.name }] : []),
                        ])
                        .flat();

                    dynamicSearchOptions = Array.from(new Map(dynamicSearchOptions.map(item => [JSON.stringify(item), item])).values());
                    
                    dynamicSearchOptions.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = String(a.dropdownLabel).toUpperCase();
                        const labelB = String(b.dropdownLabel).toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                    setSearchOptionsMasterList(dynamicSearchOptions);
                }
            } catch (error) {
                console.error("Error fetching Data Warehouses:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        const labels: Record<string, string> = {
            name: 'Warehouse Name',
            application: 'Application',
            cloudProvider: 'Cloud Provider',
            dataWarehouseType: 'Warehouse Type',
            region: 'Region',
            solutionOwner: 'Solution Owner',
            createdAt: 'Added On'
        };
        return labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };


    const globallyFilteredWarehouses =
        searchQuery.length === 0
            ? allDataWarehouses
            : allDataWarehouses.filter((warehouse: DataWarehouseJob) => {
                return searchQuery.every(queryParam => {
                    let warehouseValue: any;
                    if (queryParam.category === "application.applicationName") {
                        warehouseValue = warehouse.application?.applicationName;
                    } else if (queryParam.category === "solutionOwner.name") {
                        warehouseValue = warehouse.solutionOwner?.name;
                    } else if (queryParam.category === "details.dataWarehouse.region") {
                        warehouseValue = warehouse.details?.dataWarehouse?.region;
                    } else {
                        warehouseValue = warehouse[queryParam.category as keyof DataWarehouseJob];
                    }

                    if (warehouseValue === null || warehouseValue === undefined) return false;
                    return String(warehouseValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
                });
            });

    const pendingWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'pending' || job.status === 'submitted' || job.status === 'provisioning');
    const activeWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'active' || job.status === 'approved' || job.status === 'success');
    const rejectedWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'rejected' || job.status === 'failed');

    const totalWarehousesOverall = allDataWarehouses.length;
    const totalMatchingSearch = globallyFilteredWarehouses.length;

    const canAddWarehouse = user.roles.includes("cluster_owner") || user.roles.includes("viewer");

    const handleAddWarehouse = () => {
        navigate("/datasolutions/data-warehouses/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"datasolutions-data-warehouses"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"datasolutions-data-warehouses"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{ xs: 1.5, sm: 2 }} mb="18px" >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx: 2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Data Warehouses
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {canAddWarehouse && (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "black", color: "white", padding: "8px 24px",
                                            fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                            borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
                                        }}
                                        onClick={handleAddWarehouse}
                                        disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                    >
                                        Add New Data Warehouse
                                    </Button>
                                )}
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{
                                        padding: 0,
                                        border: 'none',
                                        ml: 2,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/Help.svg"
                                        alt="Help"
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            display: 'block',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by name, type, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                                            sx={{
                                                                border: "1px solid #bbb",
                                                                borderRadius: "10px",
                                                                height: "45px",
                                                                width: "45px",
                                                            }}
                                                            onClick={handleColumnFilterClick}
                                                        >
                                                            <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                                                        </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{ xs: 1, sm: 2 }}>
                        {totalWarehousesOverall > 0 && totalMatchingSearch > 0 && (
                            <>
                                <Accordion defaultExpanded={pendingWarehouses.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={pendingWarehouses.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending ({pendingWarehouses.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataWarehousesTable jobs={pendingWarehouses} columnFilters={columnFilters} statusType="pending"/></AccordionDetails></Accordion>
                                <Accordion defaultExpanded={activeWarehouses.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={activeWarehouses.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active ({activeWarehouses.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataWarehousesTable jobs={activeWarehouses} columnFilters={columnFilters} statusType="active"/></AccordionDetails></Accordion>
                                <Accordion defaultExpanded={rejectedWarehouses.length > 0} elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={rejectedWarehouses.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected ({rejectedWarehouses.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataWarehousesTable jobs={rejectedWarehouses} columnFilters={columnFilters} statusType="rejected"/></AccordionDetails></Accordion>
                            </>
                        )}
                        {totalWarehousesOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Warehouses Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Warehouses match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}
                        {totalWarehousesOverall === 0 && !isLoading && (
                            <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Warehouses" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Warehouses are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first Data Warehouse.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataWarehouses;



// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Menu,
//     Checkbox,
//     FormControlLabel,
//     FormGroup,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     CircularProgress,
//     Paper,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";
// import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

// import DataWarehousesTable from "./DataWarehousesTable"; // Renamed from FineTuningTable
// import { useAuth } from "../../../helpers/AuthProvider";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MainDrawer from "../../MainDrawer";
// import MiniBot from "../../MiniBot";

// // Define types for owner and application details for a DataWarehouse
// interface DataWarehouseParticipantInfo {
//     id: string;
//     name: string;
// }

// // Specific details for a DataWarehouse
// interface DataWarehouseSpecificDetails {
//     projectId?: string;
//     datasetId?: string;
//     region?: string;
//     pricingModel?: string;
// }

// // Main DataWarehouse interface
// interface DataWarehouseJob {
//     id: string;
//     name: string;
//     cloudProvider?: string;
//     solutionCategoryType?: string; // Changed from solutionType
//     costLabelPairs?: { cost: string; label: string }[];
//     status: 'pending' | 'active' | 'rejected' | string;
//     details?: {
//         dataWarehouse?: DataWarehouseSpecificDetails; // Changed from fineTuning
//     };
//     reasonForRejection?: string;
//     createdAt?: string;
//     updatedAt?: string;
//     solutionOwner?: DataWarehouseParticipantInfo | null;
// }

// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: keyof DataWarehouseJob | "solutionOwner.name" | "details.dataWarehouse.region" | string;
//     groupBy: string;
// }

// const DataWarehouses: React.FC = () => {
//     const [allDataWarehouses, setAllDataWarehouses] = useState<DataWarehouseJob[]>([]);
//     const [isLoading, setIsLoading] = useState(true);
//     const { user } = useAuth();
//     const navigate = useNavigate();
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
//     const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);

//     const initialColumnFilters: Record<string, boolean> = {
//         name: true,
//         cloudProvider: true,
//         dataWarehouseType: true,
//         region: true,
//         solutionOwner: true,
//         createdAt: true,
//     };
//     const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
//     const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             try {
//                 // Modified GraphQL query for Data Warehouses
//                 const response = await queryGraphql(`
//                     query GetDataWarehouses {
//                       getDataWarehouses {
//                         id
//                         name
//                         cloudProvider
//                         solutionCategoryType
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           dataWarehouse {
//                             projectId
//                             datasetId
//                             region
//                             pricingModel
//                           }
//                         }
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `);

//                 const dataWarehousesData = response.getDataWarehouses;
//                 console.log("data warehouse data", dataWarehousesData);
//                 if (dataWarehousesData) {
//                     const processedWarehouses = dataWarehousesData.map((dw: any): DataWarehouseJob => ({
//                         id: dw.id,
//                         name: dw.name || "Unnamed Warehouse",
//                         cloudProvider: dw.cloudProvider,
//                         solutionCategoryType: dw.solutionCategoryType,
//                         costLabelPairs: dw.costLabelPairs,
//                         status: (dw.status || 'pending').toLowerCase(),
//                         details: dw.details,
//                         reasonForRejection: dw.reasonForRejection,
//                         createdAt: dw.createdAt,
//                         updatedAt: dw.updatedAt,
//                         solutionOwner: dw.solutionOwner ? {
//                             id: dw.solutionOwner.id,
//                             name: dw.solutionOwner.name,
//                         } : null,
//                     }));
//                     setAllDataWarehouses(processedWarehouses);

//                     let dynamicSearchOptions: SearchOptions[] = processedWarehouses
//                         .map((warehouse: DataWarehouseJob) => [
//                             { category: "name", groupBy: "WAREHOUSE NAME", chipLabel: `Name: ${warehouse.name}`, dropdownLabel: warehouse.name },
//                             { category: "status", groupBy: "STATUS", chipLabel: `Status: ${warehouse.status}`, dropdownLabel: warehouse.status.charAt(0).toUpperCase() + warehouse.status.slice(1) },
//                             ...(warehouse.cloudProvider ? [{ category: "cloudProvider" as any, groupBy: "CLOUD PROVIDER", chipLabel: `Cloud: ${warehouse.cloudProvider}`, dropdownLabel: warehouse.cloudProvider }] : []),
//                             ...(warehouse.solutionCategoryType ? [{ category: "solutionCategoryType" as any, groupBy: "WAREHOUSE TYPE", chipLabel: `Type: ${warehouse.solutionCategoryType}`, dropdownLabel: warehouse.solutionCategoryType }] : []),
//                             ...(warehouse.details?.dataWarehouse?.region ? [{ category: "details.dataWarehouse.region" as any, groupBy: "REGION", chipLabel: `Region: ${warehouse.details.dataWarehouse.region}`, dropdownLabel: warehouse.details.dataWarehouse.region }] : []),
//                             ...(warehouse.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${warehouse.solutionOwner.name}`, dropdownLabel: warehouse.solutionOwner.name }] : []),
//                         ])
//                         .flat();

//                     dynamicSearchOptions = dynamicSearchOptions.filter(
//                         (obj, index, self) =>
//                             index === self.findIndex((t) =>
//                                 t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                             )
//                     );
//                     dynamicSearchOptions.sort((a, b) => {
//                         const groupA = a.groupBy.toUpperCase();
//                         const groupB = b.groupBy.toUpperCase();
//                         if (groupA > groupB) return 1;
//                         if (groupA < groupB) return -1;
//                         const labelA = String(a.dropdownLabel).toUpperCase();
//                         const labelB = String(b.dropdownLabel).toUpperCase();
//                         if (labelA > labelB) return 1;
//                         if (labelA < labelB) return -1;
//                         return 0;
//                     });
//                     setSearchOptionsMasterList(dynamicSearchOptions);
//                 }
//             } catch (error) {
//                 console.error("Error fetching Data Warehouses:", error);
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//     }, []);

//     const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
//         setTempColumnFilters(columnFilters);
//         setColumnFilterAnchorEl(event.currentTarget);
//     };

//     const handleColumnFilterClose = () => {
//         setColumnFilterAnchorEl(null);
//     };

//     const handleTempColumnFilterChange = (column: string) => {
//         setTempColumnFilters((prevFilters) => ({
//             ...prevFilters,
//             [column]: !prevFilters[column],
//         }));
//     };

//     const handleApplyColumnFilters = () => {
//         setColumnFilters(tempColumnFilters);
//         handleColumnFilterClose();
//     };

//     const getDisplayLabelForKey = (key: string): string => {
//         if (key === 'name') return 'Warehouse Name';
//         if (key === 'cloudProvider') return 'Cloud Provider';
//         if (key === 'dataWarehouseType') return 'Warehouse Type';
//         if (key === 'region') return 'Region';
//         if (key === 'solutionOwner') return 'Solution Owner';
//         if (key === 'createdAt') return 'Added On';

//         const result = key.replace(/([A-Z])/g, ' $1');
//         return result.charAt(0).toUpperCase() + result.slice(1).trim();
//     };


//     const globallyFilteredWarehouses =
//         searchQuery.length === 0
//             ? allDataWarehouses
//             : allDataWarehouses.filter((warehouse: DataWarehouseJob) => {
//                 return searchQuery.every(queryParam => {
//                     let warehouseValue: any;
//                     if (queryParam.category === "solutionOwner.name") {
//                         warehouseValue = warehouse.solutionOwner?.name;
//                     } else if (queryParam.category === "details.dataWarehouse.region") {
//                         warehouseValue = warehouse.details?.dataWarehouse?.region;
//                     } else {
//                         warehouseValue = warehouse[queryParam.category as keyof DataWarehouseJob];
//                     }

//                     if (warehouseValue === null || warehouseValue === undefined) return false;
//                     return String(warehouseValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
//                 });
//             });

//     const pendingWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'pending' || job.status === 'submitted' || job.status === 'provisioning');
//     const activeWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'active' || job.status === 'approved' || job.status === 'success');
//     const rejectedWarehouses = globallyFilteredWarehouses.filter(job => job.status === 'rejected' || job.status === 'failed');

//     const totalWarehousesOverall = allDataWarehouses.length;
//     const totalMatchingSearch = globallyFilteredWarehouses.length;

//     const canAddWarehouse = user.roles.includes("cluster_owner") || user.roles.includes("viewer");


//     const handleAddWarehouse = () => {
//         navigate("/datasolutions/data-warehouses/add");
//     };

//     if (isLoading) {
//         return (
//             <Box sx={{ display: "flex" }}>
//                 <MainDrawer index={"datasolutions-data-warehouses"} />
//                 <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
//                     <CircularProgress />
//                 </Box>
//             </Box>
//         );
//     }

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"datasolutions-data-warehouses"} />
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{ xs: 1.5, sm: 2 }} mb="18px" >
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                 mx: 2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Data Warehouses
//                             </Typography>
//                             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                 <Box>
//                                     {canAddWarehouse && (
//                                         <Button
//                                             variant="contained"
//                                             sx={{
//                                                 backgroundColor: "black", color: "white", padding: "8px 24px",
//                                                 fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                                 borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                             }}
//                                             onClick={handleAddWarehouse}
//                                             disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
//                                         >
//                                             Add New Data Warehouse
//                                         </Button>
//                                     )}
//                                 </Box>
//                                 <IconButton
//                                     aria-label="Help"
//                                     onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
//                                     sx={{
//                                         padding: 0,
//                                         border: 'none',
//                                         ml: 2,
//                                         '&:hover': {
//                                             backgroundColor: 'action.hover',
//                                         },
//                                     }}
//                                 >
//                                     <Box
//                                         component="img"
//                                         src="/assets/Help.svg"
//                                         alt="Help"
//                                         sx={{
//                                             width: 40,
//                                             height: 40,
//                                             display: 'block',
//                                         }}
//                                     />
//                                 </IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 2 }} />
//                         <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptionsMasterList}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 value={searchQuery}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) =>
//                                     tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
//                                     ))
//                                 }
//                                 onChange={(e, newVal) => {
//                                     setSearchQuery(newVal as SearchOptions[]);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by name, type, status, etc."
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start" sx={{ pl: 0.5 }}>
//                                                         <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
//                                         }}
//                                     />
//                                 )}
//                                 PaperComponent={(props) => <Paper elevation={3} {...props} />}
//                             />
//                             <Button
//                                 sx={{
//                                     border: "1px solid #bbb",
//                                     borderRadius: "10px",
//                                     height: "44px",
//                                     minWidth: "44px",
//                                     padding: "0px",
//                                     color: "#4B5563",
//                                     '&:hover': { backgroundColor: "#E5E7EB", borderColor: "#9CA3AF" }
//                                 }}
//                                 onClick={handleColumnFilterClick}
//                                 title="Filter Columns"
//                             >
//                                 <img src="/assets/pause.svg" alt="Filter Columns" style={{ width: "20px", height: "20px" }} />
//                             </Button>
//                             <Menu
//                                 anchorEl={columnFilterAnchorEl}
//                                 open={Boolean(columnFilterAnchorEl)}
//                                 onClose={handleColumnFilterClose}
//                                 anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
//                                 transformOrigin={{ vertical: "top", horizontal: "right" }}
//                                 PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
//                             >
//                                 <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
//                                     <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
//                                         TABLE COLUMNS
//                                     </Typography>
//                                     <FormGroup>
//                                         {Object.entries(tempColumnFilters).map(([key, value]) => (
//                                             <FormControlLabel
//                                                 key={key}
//                                                 control={
//                                                     <Checkbox
//                                                         checked={value}
//                                                         onChange={() => handleTempColumnFilterChange(key)}
//                                                         size="small"
//                                                         sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
//                                                     />
//                                                 }
//                                                 label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
//                                                 sx={{ marginBottom: '4px' }}
//                                             />
//                                         ))}
//                                     </FormGroup>
//                                     <Button
//                                         variant="contained" fullWidth onClick={handleApplyColumnFilters}
//                                         sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
//                                     >
//                                         Apply
//                                     </Button>
//                                 </Box>
//                             </Menu>
//                         </Box>
//                     </Box>

//                     <Box m={{ xs: 1, sm: 2 }}>
//                         {totalWarehousesOverall > 0 && totalMatchingSearch > 0 && (
//                             <>
//                                 {/* Pending Accordion */}
//                                 <Accordion
//                                     defaultExpanded={pendingWarehouses.length > 0}
//                                     elevation={0}
//                                     sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                                     disabled={pendingWarehouses.length === 0 && searchQuery.length > 0}
//                                 >
//                                     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                         <Box sx={{ display: "flex", alignItems: "center" }}>
//                                             <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                             <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                                 Pending Warehouses ({pendingWarehouses.length})
//                                             </Typography>
//                                         </Box>
//                                     </AccordionSummary>
//                                     <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                         {pendingWarehouses.length > 0 ? (
//                                             <DataWarehousesTable
//                                                 jobs={pendingWarehouses}
//                                                 columnFilters={columnFilters}
//                                                 statusType="pending"
//                                             />
//                                         ) : (
//                                             <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
//                                                 No pending warehouses match your filters.
//                                             </Typography>
//                                         )}
//                                     </AccordionDetails>
//                                 </Accordion>

//                                 {/* Active Accordion */}
//                                 <Accordion
//                                     defaultExpanded={activeWarehouses.length > 0}
//                                     elevation={0}
//                                     sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                                     disabled={activeWarehouses.length === 0 && searchQuery.length > 0}
//                                 >
//                                     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                         <Box sx={{ display: "flex", alignItems: "center" }}>
//                                             <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                             <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                                 Active Warehouses ({activeWarehouses.length})
//                                             </Typography>
//                                         </Box>
//                                     </AccordionSummary>
//                                     <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                         {activeWarehouses.length > 0 ? (
//                                             <DataWarehousesTable
//                                                 jobs={activeWarehouses}
//                                                 columnFilters={columnFilters}
//                                                 statusType="active"
//                                             />
//                                         ) : (
//                                             <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
//                                                 No active warehouses match your filters.
//                                             </Typography>
//                                         )}
//                                     </AccordionDetails>
//                                 </Accordion>

//                                 {/* Rejected Accordion */}
//                                 <Accordion
//                                     defaultExpanded={rejectedWarehouses.length > 0}
//                                     elevation={0}
//                                     sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                                     disabled={rejectedWarehouses.length === 0 && searchQuery.length > 0}
//                                 >
//                                     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//                                         <Box sx={{ display: "flex", alignItems: "center" }}>
//                                             <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                             <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                                 Rejected Warehouses ({rejectedWarehouses.length})
//                                             </Typography>
//                                         </Box>
//                                     </AccordionSummary>
//                                     <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
//                                         {rejectedWarehouses.length > 0 ? (
//                                             <DataWarehousesTable
//                                                 jobs={rejectedWarehouses}
//                                                 columnFilters={columnFilters}
//                                                 statusType="rejected"
//                                             />
//                                         ) : (
//                                             <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily: "Gilroy-Regular", py: 2 }}>
//                                                 No rejected warehouses match your filters.
//                                             </Typography>
//                                         )}
//                                     </AccordionDetails>
//                                 </Accordion>
//                             </>)}
//                         {totalWarehousesOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
//                             <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
//                                 <img src="/assets/inbox-icon.svg" alt="No Warehouses Match" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Data Warehouses match your search
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
//                                     Try adjusting your search terms or filters.
//                                 </Typography>
//                             </Box>
//                         )}

//                         {totalWarehousesOverall === 0 && !isLoading && (
//                             <Box
//                                 display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                 sx={{
//                                     textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                     backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                     boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
//                                 }}
//                             >
//                                 <img src="/assets/inbox-icon.svg" alt="No Warehouses" style={{ width: '100px', marginBottom: '20px' }} />
//                                 <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                     No Data Warehouses are added yet
//                                 </Typography>
//                                 <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                     Click the button above to add your first Data Warehouse.
//                                 </Typography>
//                             </Box>
//                         )}
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default DataWarehouses;



// // Filename: RAG.tsx (or RAGPage.tsx as you have it)
// import React from "react"; // Removed unused useState
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     IconButton,
// } from "@mui/material";


// import { useAuth } from "../../../helpers/AuthProvider";
// import MainDrawer from "../../MainDrawer";
// import MiniBot from "../../MiniBot";

// // All dynamic data fetching, state management for RAGs, search, and column filters are removed
// // as RAGTable will now display static, hardcoded data and not accept data props.

// const DataWarehouses: React.FC = () => {
//     const { user } = useAuth();
//     const navigate = useNavigate();

//     const canAddDataWarehouses = true; // Example permission, adjust as needed

//     const handleAddDataWarehouses = () => {
//         // Navigate to an add page or handle as a placeholder for a static view
//         navigate("/datasolutions/data-warehouses/add"); // Adjust this path if necessary
//         // For a truly static page without add functionality, you might comment out the onClick or the button.
//     };

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"datasolutions-data-warehouses"} /> {/* Ensure 'rag' is a valid index for your MainDrawer */}
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                 mx:2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Data Warehouses 
//                             </Typography>
//                             <Box sx={{display:'flex', justifyContent:'center'}}>
//                             {canAddDataWarehouses && (
//                                 <Button
//                                     variant="contained"
//                                     sx={{
//                                         backgroundColor: "black", color: "white", padding: "8px 24px",
//                                         fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                         borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                     }}
//                                     onClick={handleAddDataWarehouses}
//                                     // Adjust disabled logic based on DataWarehouses-specific permissions if different
//                                     disabled = {(user.roles.includes("cluster_owner") || user.roles.includes("viewer")) ? false : true}
//                                 >
//                                     Add New Data Warehouse
//                                 </Button>
//                             )}
//                             <IconButton
// 									  aria-label="Help" 
// 									  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
// 									  sx={{
// 										padding: 0, 
// 										border: 'none',  
// 										ml:2,
// 										'&:hover': {
// 										  backgroundColor: 'action.hover', 
// 										},
										
// 									  }}
// 									>
// 									  <Box
// 										component="img"
// 										src="/assets/Help.svg" 
// 										alt="" 
// 										sx={{
// 										  width: 40,  
// 										  height: 40,
// 										  display: 'block', 
// 										}}
// 									  />
// 									</IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         {/* Search and Filter UI have been removed as the table is now static */}
//                     </Box>

//                     <Box m={{xs: 1, sm:2}}>
//                         {/* RAGTable component will render its own static data */}
//                         <Box
//                                                             display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                                             sx={{
//                                                                 textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                                                 backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                                                 boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
//                                                                 height:"70vh"
//                                                             }}
//                                                         >
//                                                             <img src="/assets/inbox-icon.svg" alt="No AI Agents" style={{ width: '100px', marginBottom: '20px' }} />
//                                                             <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                                                 No Data Warehouse added yet
//                                                             </Typography>
//                                                             <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                                                 Click the button above to add your first Data Warehouse.
//                                                             </Typography>
//                                                         </Box>
                      
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default DataWarehouses;
// // import { Box, Typography } from "@mui/material";
// // import React, { useState } from "react";
// // import { useNavigate } from "react-router-dom";
// // import DashBoardPage from "../DashBoardPage";
// // import MainDrawer from "../MainDrawer";
// // import ComingSoonPage from "../ComingSoonPage";
// // const DataWarehouses: React.FC = () => {
// //     return(<Box sx={{ display: "flex"}}>
// //     <MainDrawer index={"datasolutions-data-warehouses"} />
// // {/* Helllo */}
// //     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100%", width:"100%"}}>
// //  {/* <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography> */}
// //  <ComingSoonPage/>
// //     </Box>
// //     </Box>)
// // }
// // export default DataWarehouses;